// src/components/subscription/SubscriptionBadge.tsx
import React from 'react';
import { Crown, AlertTriangle, Check } from 'lucide-react';

export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled';
export type PlanType = 'free' | 'medium' | 'premium';

interface SubscriptionBadgeProps {
  plan: PlanType;
  status: SubscriptionStatus;
  isExpiringSoon?: boolean;
  daysRemaining?: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
  plan,
  status,
  isExpiringSoon = false,
  daysRemaining,
  size = 'md',
  showLabel = false,
  className = ''
}) => {
  // Size mappings for the icon
  const sizeMap = {
    sm: 12,
    md: 16,
    lg: 20
  };

  // Determine badge colors based on plan and status
  const getBadgeColors = () => {
    if (status === 'past_due') {
      return 'bg-red-100 text-red-700 border-red-200';
    }
    
    if (status === 'canceled') {
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
    
    if (isExpiringSoon) {
      return 'bg-amber-100 text-amber-700 border-amber-200';
    }

    switch (plan) {
      case 'premium':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'medium':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'free':
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Determine the icon to display
  const renderIcon = () => {
    if (status === 'past_due') {
      return <AlertTriangle size={sizeMap[size]} />;
    }
    
    if (plan === 'free') {
      return status === 'canceled' ? 
        <AlertTriangle size={sizeMap[size]} /> : 
        <Check size={sizeMap[size]} />;
    }
    
    return <Crown size={sizeMap[size]} />;
  };

  // Get display label
  const getLabel = () => {
    if (status === 'past_due') return 'Payment Due';
    if (status === 'canceled') return 'Canceled';
    if (status === 'trialing') return `Trial (${daysRemaining} days)`;
    
    switch (plan) {
      case 'premium':
        return 'Premium';
      case 'medium':
        return 'Medium';
      case 'free':
      default:
        return 'Free';
    }
  };

  return (
    <div 
      className={`flex items-center gap-1 px-2 py-1 rounded-full border ${getBadgeColors()} ${className}`}
      title={`${getLabel()}${isExpiringSoon ? ` - Renews in ${daysRemaining} days` : ''}`}
    >
      {renderIcon()}
      {showLabel && (
        <span className={`text-${size === 'sm' ? 'xs' : 'sm'} font-medium`}>
          {getLabel()}
        </span>
      )}
    </div>
  );
};

export default SubscriptionBadge;